const appendJapaneseUiSuffix = (label: string, suffix: string): string =>
  /[A-Za-z0-9]$/.test(label) ? `${label} ${suffix}` : `${label}${suffix}`;

export const jaMessages = {
  localeLabel: "日本語",
  navigation: {
    sectionsAria: "ゲームセクション",
    clickHint: "click!",
    tapHint: "tap!",
    draftPlaceholder: (label: string) => `${label} (準備中)`,
    items: {
      hovTarget: { label: "Hov", screenLabel: "Hov biome" },
      timTarget: { label: "Tim", screenLabel: "Tim biome" },
      coreloop3: { label: "Mat", screenLabel: "Mat biome" },
      coreloop4: { label: "Lev", screenLabel: "Lev biome" },
      mpm: { label: "MPM", screenLabel: "MPM" },
      achievements: { label: "実績", screenLabel: "実績" },
      records: { label: "記録", screenLabel: "記録" },
      help: { label: "ヘルプ", screenLabel: "ヘルプ" },
      settings: { label: "設定", screenLabel: "設定" },
      payment: { label: "ショップ", screenLabel: "ショップ" },
    },
  },
  standby: {
    label: "待機モード",
    resumeAriaLabel: "待機モードを終了してゲーム画面に戻る",
  },
  common: {
    purchased: "購入済み",
    notPurchased: "未購入",
    receive: "受け取る",
    purchase: "購入",
    loading: "読み込み中...",
    languageChangedReloading: "言語を変更しました。再読み込みします。",
    hintLabel: "説明",
    toastCloseAria: "通知を閉じる",
    formatMilliseconds: (milliseconds: number) => `${milliseconds}ミリ秒`,
  },
  mpm: {
    title: "MPM",
    subtitle: "Mouse Pointer 管理",
    hovTargetsTitle: "Hov ターゲット",
    presets: {
      title: "プリセット",
      unsaved: "未保存",
      assignedMp: (amount: number) => `MP ${amount} 割当`,
      save: "保存",
      load: "読込",
      saveAriaLabel: (presetNumber: number) =>
        `現在の MPM 状態をプリセット ${presetNumber} に保存`,
      loadAriaLabel: (presetNumber: number) =>
        `MPM プリセット ${presetNumber} を読込`,
    },
    sections: {
      targets: "ターゲット",
      upgrades: "アップグレード",
      mhovExecution: "MHov",
      nhovExecution: "NHov",
      ohovExecution: "OHov",
      exploration: "探索",
      amplifierPanels: "増幅器パネル",
      amplifierUpgrades: "増幅器アップグレード",
    },
    actions: {
      clearAll: "全解除",
      assignAll: "全割当",
    },
    mhovExecutionInputs: {
      multiplier: "最小 HG (x)",
      minimumResetIntervalSeconds: "最小時間 (s)",
    },
    nhovExecutionInputs: {
      minimumEffect: {
        hgAmplify: "最小 HGU (x)",
        hgDiscount: "最小 HGC (/)",
        hgNHovEffectsToHng: "最小 HG NHov (x)",
        hngScale: "最小 HnG RS (x)",
      },
      minimumResetIntervalSeconds: "最小時間 (s)",
    },
    ohovExecutionInputs: {
      minimumHovCore: "最小 HC",
      minimumResetIntervalSeconds: "最小時間 (s)",
    },
    assignmentTooltips: {
      missingMp: (missingMp: number) => `${missingMp} MP 不足`,
    },
    clearAllAriaLabel: (section: string) =>
      appendJapaneseUiSuffix(section, "の MP 割当をすべて解除"),
    assignAllAriaLabel: (section: string) =>
      appendJapaneseUiSuffix(section, "の未割当 MP slot をすべて割当"),
    sectionEnabledAriaLabel: (section: string) =>
      appendJapaneseUiSuffix(section, "セクションは有効"),
    sectionDisabledAriaLabel: (section: string) =>
      appendJapaneseUiSuffix(section, "セクションは停止中"),
    mpAvailabilityAriaLabel: (available: string, capacity: string) =>
      `MP 残量 ${available} / ${capacity}`,
  },
  tutorial: {
    ok: "OK",
    progressLabel: "進行",
    wellcome: "Hover Idle へようこそ！",
    pcSteps: {
      intro:
        "このゲームでは、マウスポインターを重ねた場所にホバー効果が発生します。",
      target: "画面真ん中のターゲットにホバーすると Hov を獲得できます。",
      upgrade:
        "画面左下のアップグレードボタンにホバーすると、コストを支払って HG（Hov 獲得量）を強化できます。",
      action: "まずは、OK ボタンにマウスポインターを重ねてみましょう。",
    },
    mobileSteps: {
      intro:
        "このゲームでは、仮想ポインターを置いた場所にホバー効果が発生します。",
      action: "まずは OK をタップして、置いてみましょう。",
    },
    mobileNotice:
      "仮想ポインターは、最後に置いた場所を覚えています。画面を戻したときも、同じホバー対象に復帰します。",
  },
  settings: {
    title: "設定",
    categoriesAria: "設定カテゴリ",
    tabs: {
      general: "全般",
      gameplay: "ゲーム",
      display: "表示",
      notifications: "通知",
      data: "データ",
    },
    general: {
      sectionTitle: "全般",
      languageLabel: "表示言語",
      languageDescription: "変更後に再読み込みして反映します。",
      languageOptions: {
        ja: "日本語",
        en: "英語",
      },
      hoverToggleDurationLabel: "ホバートグル時間",
      hoverToggleDurationDescription:
        "MP 割当や TimHov 活性切替など、ホバーで切り替える操作に必要な時間です。",
      hoverToggleDurationOptions: {
        short: "1秒",
        normal: "2秒",
      },
      hoverChargeDurationLabel: "ホバーチャージ時間",
      hoverChargeDurationDescription:
        "全割当・全解除やプリセットの保存・読込など、一定時間ホバーして実行する操作に必要な時間です。",
      hoverChargeDurationOptions: {
        short: "1秒",
        normal: "2秒",
      },
    },
    gameplay: {
      sectionTitle: "ゲーム",
      mHov: {
        sectionTitle: "MHov",
        chargeDurationLabel: "実行までの時間",
        chargeDurationOptions: {
          "0": "0秒",
          "2000": "2秒",
          "5000": "5秒",
        },
        chargeDurationHint:
          "ボタンをホバーしてから MHov が実行されるまでの時間です。",
        animationSpeedLabel: "アニメーション速度",
        animationSpeedOptions: {
          instant: "無し",
          fast: "早い",
          normal: "普通",
        },
        animationSpeedReduceMotionTooltip:
          "省演出中は「無し」として扱われます。",
      },
      nHov: {
        sectionTitle: "NHov",
        chargeDurationLabel: "実行までの時間",
        chargeDurationOptions: {
          "0": "0秒",
          "2000": "2秒",
          "5000": "5秒",
        },
        chargeDurationHint:
          "ボタンをホバーしてから NHov が実行されるまでの時間です。",
        animationSpeedLabel: "アニメーション速度",
        animationSpeedOptions: {
          instant: "無し",
          fast: "早い",
          normal: "普通",
        },
        animationSpeedReduceMotionTooltip:
          "省演出中は「無し」として扱われます。",
      },
      oHov: {
        sectionTitle: "OHov",
        chargeDurationLabel: "実行までの時間",
        chargeDurationOptions: {
          "0": "0秒",
          "2000": "2秒",
          "5000": "5秒",
        },
        chargeDurationHint:
          "ボタンをホバーしてから OHov が実行されるまでの時間です。",
        burstAnimationLabel: "アニメーション",
        burstAnimationOptions: {
          on: "オン",
          off: "オフ",
        },
        burstAnimationReduceMotionTooltip:
          "省演出中は「オフ」として扱われます。",
      },
    },
    display: {
      sectionTitle: "表示",
      reduceMotion: "省演出",
      reduceMotionHint:
        "非必須のアニメーションを抑制します。MHov 実行までの時間など、誤操作防止のための待機時間には影響しません。",
      lowPowerMode: "低電力モード",
      lowPowerModeHint:
        "ゲーム進行と入力受付のタイミングを変えず、foreground の描画頻度を約30 fps に抑えます。",
      notationLabel: "数値表記",
      notationOptions: {
        standard: "標準 (B, T, Qa...)",
        scientific: "科学表記 (1.23e10)",
        engineering: "工学表記 (12.3e9)",
      },
    },
    notifications: {
      sectionTitle: "通知",
      achievementNotificationLabel: "実績通知",
      wallNotificationLabel: "壁通知",
      helpNotificationsLabel: "ヘルプ通知",
      helpNotificationsDescription:
        "新しいヘルプ項目が追加されたときに通知を表示します。",
      achievementNotificationOptions: {
        off: "無し",
        dot: "簡易",
        full: "有り",
      },
      wallNotificationOptions: {
        off: "無し",
        dot: "簡易",
        full: "有り",
      },
    },
    data: {
      sectionTitle: "データ",
      backupHeading: "セーブデータ",
      backupDescription:
        "セーブデータをファイルに書き出したり、読み込んで復元できます。",
      backupButton: "Backup (書き出し)",
      loadButton: "Load (読み込み)",
      backupSucceeded: "バックアップファイルを保存しました。",
      backupCancelled: "保存をキャンセルしました。",
      backupFailed: (reason: string) => `バックアップに失敗しました: ${reason}`,
      loadFailed: (reason: string) => `読み込みに失敗しました: ${reason}`,
      rewardCodeHeading: "リワードコード",
      rewardCodeDescription:
        "コードを入力すると報酬を受け取れます。受け取り状況は、進行データを初期化しても保持されます。",
      rewardCodePlaceholder: "コードを入力",
      rewardCodeAria: "リワードコード",
      tokenSummary: (token: string) => `所持 Token: ${token}`,
      rewardCodeEntry: (id: string, token: string) => `${id}: ${token} Token`,
      redeemed: "受取済み",
      notRedeemed: "未受取",
      rewardCodeEmpty: "リワードコードを入力してください。",
      rewardCodeApplied: "リワードコードを適用しました。",
      rewardCodeAlreadyRedeemed: "このリワードコードは受取済みです。",
      rewardCodeInvalid: "無効なリワードコードです。",
      dangerHeading: "危険な操作",
      dangerDescription:
        "通常進行だけを消去し、最初からやり直します。リワードコードと Shop の課金状態は保持されます。",
      eraseButton: "進行データを初期化",
      eraseButtonConfirm: "もう一度クリックで初期化",
      eraseWarning: (seconds: number) =>
        `警告: ${seconds} 秒以内にもう一度押すと進行データが消去されます。`,
      eraseCancelled: "進行データの初期化はキャンセルされました。",
      eraseFailed: (reason: string) =>
        `進行データの初期化に失敗しました: ${reason}`,
      eraseInProgress: "進行データを初期化中...",
      account: {
        title: "アカウント",
        statusGuest: "現在はゲストとしてプレイ中です。",
        statusGuestHint:
          "サインインすると、セーブデータや購入状態を復元しやすくなります。",
        statusSignedIn: (identifier: string) =>
          `${identifier} でサインイン中です。`,
        statusSignedInHint:
          "セーブデータと購入状態はアカウントに紐付いています。",
        emailLabel: "メールアドレス",
        emailPlaceholder: "you@example.com",
        emailSubmit: "サインインリンクを送信",
        emailSent: "確認メールを送信しました。受信トレイをご確認ください。",
        oauthSubmit: (provider: string) => `${provider} でサインイン`,
        oauthNotImplemented: "OAuth サインインは現在準備中です。",
        errors: {
          invalidEmail: "メールアドレスの形式を確認してください。",
          noAnonymousSession: "ゲストセッションが見つかりません。",
          linkFailed:
            "サインインに失敗しました。時間をおいて再試行してください。",
          generic: "予期しないエラーが発生しました。",
        },
      },
    },
    draftPlaceholder: (label: string) => `${label} (準備中)`,
  },
  offline: {
    summary: (elapsed: string, timGain: string | null) =>
      timGain === null
        ? `オフライン報酬: 離席時間 ${elapsed}`
        : `オフライン報酬: 離席時間 ${elapsed} / +${timGain} Tim 獲得`,
    rewardUnavailable: () =>
      "オフライン報酬を受け取れませんでした。セーブデータを再読み込みして、もう一度お試しください。",
    rewardCommitRejected: () =>
      "オフライン報酬を保存できませんでした。セーブデータを再読み込みして、もう一度お試しください。",
    durationHoursMinutes: (hours: string, minutes: string) =>
      `${hours}時間${minutes}分`,
    durationMinutesSeconds: (minutes: string, seconds: string) =>
      `${minutes}分${seconds}秒`,
    durationSeconds: (seconds: string) => `${seconds}秒`,
    modal: {
      title: "オフライン報酬",
      elapsedLabel: (elapsed: string) => `離席時間: ${elapsed}`,
      appliedNotice: "※ 上限のため、24時間分までを報酬対象としました。",
      timGainLabel: (amount: string) => `+${amount} Tim`,
      hoverAnchorNotice: "仮想ポインターが前回のホバー対象に復帰しました。",
      close: "閉じる",
      hoverClose: "ホバーで閉じる",
      closeAriaLabel: "オフライン報酬を閉じる",
    },
  },
  achievements: {
    tabsAria: "実績カテゴリ",
    tabs: {
      normal: "通常",
      secret: "シークレット",
    },
    unlocked: "解除済み",
    locked: "未解除",
    unlockedNotice: (names: string) => `実績解除: ${names}`,
    hovGainCountMultiplierLabel: (multiplier: number) =>
      `実績ボーナス: ×${multiplier.toFixed(2)} HG`,
    timGainCountMultiplierLabel: (multiplier: number) =>
      `実績ボーナス: ×${multiplier.toFixed(2)} TG`,
    names: {
      thousand: "千",
      "first-merge-hov": "Hov を統合",
      "second-merge-hov": "もう一度 Hov を統合",
      pop: "ポンッ！",
      bomb: "ボンッ！",
      "hov-gain-family-upgrade": "ホバーで強化",
      "tim-hov-activation": "TimHov 活性制御",
      "time-has-come": "時は来た",
      "global-hov-gain-upgrade-milestone": "HG マイルストーン",
      "ninth-hov-gain": "9番 Hov 獲得量",
      "first-new-hov": "新しい Hov",
      "multi-branch-new-hov": "新しい新しい Hov",
      "many-new-hov": "たくさんの新しい Hov",
      "skip-hov-gain": "HG スキップ",
      "one-cost-batch-upgrade": "1コス全部くれ",
      "merge-hov-only-one-hov-target-cell": "統合？",
      "new-hov-without-merge-hov": "Hov を統合せずに新しい Hov",
      "outside-mhov": "MHov の外側",
      "outside-nhov": "NHov の外側",
      "amplifier-upgrade": "増幅強化",
      "hov-1-gain-gets-serious": "僕も本気出す",
      "mass-hov-gain-family-upgrade": "大量にホバーで強化",
      "music-pointer": "音楽ポインター",
      "movie-pointer": "動画ポインター",
      "maschinen-pointer": "機械式ポインター",
      "hov-9-gain-upgrade-milestone": "H9G マイルストーン",
      vigintillion: "Vigintillion",
      "hov-1-gain-tries-too": "僕も頑張る",
      google: "Google",
      "hov-gain-family-upgrade-marathon": "いつまでもホバーで強化",
      "global-hov-gain-upgrade-tenth-milestone": "HG の高まり",
      "not-fully-free": "100%オフではない",
      "nhov-knows-nhov": "NHov は NHov を知っている",
      "first-outside-hov": "Hov の外側へ",
      "red-amplifier-full-charge": "蓄光",
      "massive-merge-hov": "どっしりと Hov を統合",
      "rank-gap": "ランクギャップ",
      "second-outside-hov": "また Hov の外側へ",
      "fast-outside-hov": "早く Hov の外側へ",
      "thousand-ap": "増幅",
      "tenth-outside-hov": "もっと Hov の外へ",
      "mhov-mp-execution-ten": "統合は任せた",
      "quick-outside-hov": "すばやく Hov の外へ",
      "swift-outside-hov": "急いで Hov の外へ",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "HovHov",
      "fourth-hov-gain": "4番 Hov 獲得量",
      "cheap-fourth-hov-gain": "安くなってきた",
      "cheap-global-hov-gain-milestone": "安い安い安い",
      billion: "億",
      "burn-the-core": "核を燃やして",
      "mp-wall-preset": "MP の壁を越えて",
      "exploration-tim-acceleration": "借りた時間",
      "exploration-biome-level-two": "望遠",
    },
    shortLabels: {
      thousand: "1k",
      "first-merge-hov": "MH",
      "second-merge-hov": "MH2",
      pop: "P",
      bomb: "B",
      "hov-gain-family-upgrade": "HU",
      "tim-hov-activation": "TH",
      "time-has-come": "Tim+",
      "global-hov-gain-upgrade-milestone": "HGM",
      "ninth-hov-gain": "H9G",
      "first-new-hov": "NH",
      "multi-branch-new-hov": "NNH",
      "many-new-hov": "NH10",
      "skip-hov-gain": "SHG",
      "one-cost-batch-upgrade": "1C",
      "merge-hov-only-one-hov-target-cell": "MH?",
      "new-hov-without-merge-hov": "NWM",
      "outside-mhov": "OM",
      "outside-nhov": "ON",
      "amplifier-upgrade": "AU",
      "hov-1-gain-gets-serious": "H1!",
      "mass-hov-gain-family-upgrade": "HU+",
      "music-pointer": "MP3",
      "movie-pointer": "MP4",
      "maschinen-pointer": "MP5",
      "hov-9-gain-upgrade-milestone": "H9GM",
      vigintillion: "VG",
      "hov-1-gain-tries-too": "H1+",
      google: "G",
      "hov-gain-family-upgrade-marathon": "HU*",
      "global-hov-gain-upgrade-tenth-milestone": "HG1k",
      "not-fully-free": "NH%",
      "nhov-knows-nhov": "NKN",
      "first-outside-hov": "OH",
      "red-amplifier-full-charge": "LU",
      "massive-merge-hov": "MMH",
      "rank-gap": "GAP",
      "second-outside-hov": "OH2",
      "fast-outside-hov": "FOH",
      "thousand-ap": "1kA",
      "tenth-outside-hov": "OH10",
      "mhov-mp-execution-ten": "MM10",
      "quick-outside-hov": "QOH",
      "swift-outside-hov": "SOH",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "HH",
      "fourth-hov-gain": "H4G",
      "cheap-fourth-hov-gain": "CH4G",
      "cheap-global-hov-gain-milestone": "CCC",
      billion: "1b",
      "burn-the-core": "BTC",
      "mp-wall-preset": "MPW",
      "exploration-tim-acceleration": "BT",
      "exploration-biome-level-two": "LV",
    },
    conditions: {
      thousand: "1,000 Hov 所持する",
      "first-merge-hov": "初めて MHov を実行する",
      "second-merge-hov": "2回目の MHov を実行する",
      pop: (formattedThreshold: string) =>
        `${formattedThreshold} HG 以上の MHov を実行する`,
      bomb: (formattedThreshold: string) =>
        `${formattedThreshold} HG 以上の MHov を実行する`,
      "hov-gain-family-upgrade": (count: number) =>
        `HG・HnG アップグレードを${count}回実行する`,
      "tim-hov-activation": (quantity: number) =>
        `Target hover 中に Tim から Hov を${quantity}精製する`,
      "time-has-come": (quantity: number) => `累計${quantity} Tim を消費する`,
      "global-hov-gain-upgrade-milestone": (level: number) =>
        `HG アップグレードレベルが${level}に到達する`,
      "ninth-hov-gain": "9つ目の Hov ターゲットを解除する",
      "first-new-hov": "初めて NHov を実行する",
      "multi-branch-new-hov": "2種類以上の NHov を実行する",
      "many-new-hov": "合計10回 NHov を実行する",
      "skip-hov-gain":
        "1つ目以外のターゲットが解除されていない状態で、9つ目の Hov ターゲットを解除する",
      "one-cost-batch-upgrade": (level: number) =>
        `HG アップグレードレベル${level}を 1 コストで実行する`,
      "merge-hov-only-one-hov-target-cell":
        "1つの Hov セルだけで MHov を実行する",
      "new-hov-without-merge-hov": "MHov せずに NHov を実行する",
      "outside-mhov": "1.8e308 HG の MHov を実行する",
      "outside-nhov": "/1.8e308 HG コストの NHov を実行する",
      "amplifier-upgrade": (count: number) =>
        `増幅器アップグレードを${count}回実行する`,
      "hov-1-gain-gets-serious": (formattedThreshold: string) =>
        `H1G が ${formattedThreshold} 以上になる`,
      "mass-hov-gain-family-upgrade": (count: number) =>
        `HG・HnG アップグレードを合計${count}回実行する`,
      "music-pointer": (count: number) => `MP を${count}個割り当てる`,
      "movie-pointer": (count: number) => `MP を${count}個割り当てる`,
      "maschinen-pointer": (count: number) => `MP を${count}個割り当てる`,
      "hov-9-gain-upgrade-milestone": (level: number) =>
        `H9G アップグレードレベルが${level}に到達する`,
      vigintillion: (formattedThreshold: string) =>
        `${formattedThreshold} Hov 所持する`,
      "hov-1-gain-tries-too": (formattedThreshold: string) =>
        `H1G が ${formattedThreshold} 以上になる`,
      google: (formattedThreshold: string) =>
        `${formattedThreshold} Hov 所持する`,
      "hov-gain-family-upgrade-marathon": (count: number) =>
        `HG・HnG アップグレードを${count}回実行する`,
      "global-hov-gain-upgrade-tenth-milestone": (level: number) =>
        `HG アップグレードレベルが${level}に到達する`,
      "not-fully-free": (formattedThreshold: string) =>
        `/${formattedThreshold} HG コストを超える NHov を実行する`,
      "nhov-knows-nhov": (formattedThreshold: string) =>
        `HG NHov 効果を x${formattedThreshold} 以上強めて HnG に渡す`,
      "first-outside-hov": "初めて OHov が発動する",
      "red-amplifier-full-charge": "増幅器赤を最大までチャージする",
      "massive-merge-hov": (formattedThreshold: string) =>
        `1度で ${formattedThreshold} 以上の MHov を実行する`,
      "rank-gap": (formattedThreshold: string) =>
        `H9G が H1G の ${formattedThreshold} 以上になる`,
      "second-outside-hov": "2回目の OHov が発動する",
      "fast-outside-hov": (hours: number) =>
        `${hours}時間以内に OHov が発動する`,
      "thousand-ap": "1,000 AP 所持する",
      "tenth-outside-hov": "10回目の OHov が発動する",
      "mhov-mp-execution-ten": "MHov を MP が10回実行する",
      "quick-outside-hov": (minutes: number) =>
        `${minutes}分以内に OHov が発動する`,
      "swift-outside-hov": (minutes: number) =>
        `${minutes}分以内に OHov が発動する`,
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "HovTim バランスのスライダーを Hov に割り当てる",
      "fourth-hov-gain": "4つ目の Hov ターゲットを解除する",
      "cheap-fourth-hov-gain": "H4G を 1 コストで解除する",
      "cheap-global-hov-gain-milestone":
        "HG マイルストーンを 1 コストで発動する",
      billion: (formattedThreshold: string) =>
        `${formattedThreshold} Hov 所持する`,
      "burn-the-core": (count: number) => `HovCore を累計${count}消費する`,
      "mp-wall-preset": "MP の壁をクリアする",
      "exploration-tim-acceleration": (seconds: number) =>
        `探索で Tim バイオーム効果によって探索時間を累計${seconds}秒進める`,
      "exploration-biome-level-two": (level: number) =>
        `探索の各バイオームレベルが${level}に到達する`,
    },
    effectLabel: "効果: ",
    effects: {
      thousand: "",
      "first-merge-hov": "",
      "second-merge-hov": (multiplier: number) => `×${multiplier} HG`,
      pop: "",
      bomb: "",
      "hov-gain-family-upgrade":
        "HG・HnG アップグレードのホバー連続購入の開始が早くなる",
      "tim-hov-activation": "TimHov 活性を制御できるようになる",
      "time-has-come": (multiplier: number) => `x${multiplier} TimHov 活性効率`,
      "global-hov-gain-upgrade-milestone": (level: number) =>
        `HG アップグレード ${level}レベルごとに、HG マイルストーンが発動することを知る`,
      "ninth-hov-gain": (amount: number) => `+${amount} MP`,
      "first-new-hov": "",
      "multi-branch-new-hov": (multiplier: number) => `HG x${multiplier}`,
      "many-new-hov": "",
      "skip-hov-gain": "",
      "one-cost-batch-upgrade": "1コストのホバー連続強化がまとめて購入される",
      "merge-hov-only-one-hov-target-cell": "",
      "new-hov-without-merge-hov": "",
      "outside-mhov": "MHov を 1.8e308 HG 以上で実行できるようになる",
      "outside-nhov": "全ての NHov を 1.8e308 以上で実行できるようになる",
      "amplifier-upgrade":
        "増幅器アップグレードのホバー連続購入開始がかなり早くなり、増幅器アップグレードに MP を割り当てられるようになる",
      "hov-1-gain-gets-serious": (bonus: number) => `+${bonus} H1G 指数`,
      "mass-hov-gain-family-upgrade": (amount: number) =>
        `+${amount} MP。HG・HnG アップグレードに MP を割り当てられるようになる`,
      "music-pointer": (amount: number) => `+${amount} MP`,
      "movie-pointer": (amount: number) => `+${amount} MP`,
      "maschinen-pointer": (amount: number) => `+${amount} MP`,
      "hov-9-gain-upgrade-milestone": "",
      vigintillion: "",
      "hov-1-gain-tries-too": (bonus: number) => `+${bonus} H1G 指数`,
      google: (bonus: number) => `+${bonus} HG 指数`,
      "hov-gain-family-upgrade-marathon":
        "HG・HnG アップグレードのホバー連続購入の開始がさらに早くなる",
      "global-hov-gain-upgrade-tenth-milestone": (bonus: number) =>
        `+${bonus} HG 指数`,
      "not-fully-free": (divisor: number) => `/${divisor} HG・HnG コスト`,
      "nhov-knows-nhov": "",
      "first-outside-hov": "",
      "red-amplifier-full-charge": "x1.1 RAG",
      "massive-merge-hov": (bonus: number) => `+${bonus} HG 指数`,
      "rank-gap": (bonus: number) => `+${bonus} HG 指数`,
      "second-outside-hov": "",
      "fast-outside-hov": "",
      "thousand-ap": "",
      "tenth-outside-hov": (bonus: number) => `+${bonus} HG 指数`,
      "mhov-mp-execution-ten": (bonus: number) => `+${bonus} MP`,
      "quick-outside-hov": "",
      "swift-outside-hov": "",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "",
      "fourth-hov-gain": (amount: number) => `+${amount} MP`,
      "cheap-fourth-hov-gain": (divisor: number) =>
        `/${divisor} HG・HnG コスト`,
      "cheap-global-hov-gain-milestone": (divisor: number) =>
        `/${divisor} HG・HnG コスト`,
      billion: "",
      "burn-the-core": (skillCoordinate: string) =>
        `スキル${skillCoordinate}が OHov 数に応じて強化される`,
      "mp-wall-preset": "MPプリセット +1",
      "exploration-tim-acceleration": "探索加速を利用できるようになる",
      "exploration-biome-level-two": (amount: number) => `+${amount} MP`,
    },
    secrets: {
      maskedName: "???",
      maskedShortLabel: "?",
      names: {
        observer: "観察者",
        "heavy-pointer": "重いポインター",
        "x-pattern-hover": "X",
        "hov-o-label": "hOver",
        "h-pattern-unlock": "H は Hov の H",
        "h-pattern-hover": "H は Hover の H",
        "target-rapid-click": "これはクッキーではありません",
      },
      shortLabels: {
        observer: "OBS",
        "heavy-pointer": "HM",
        "x-pattern-hover": "X",
        "hov-o-label": "hO",
        "h-pattern-unlock": "H",
        "h-pattern-hover": "H",
        "target-rapid-click": "TC",
      },
      hints: {
        observer: "焦らなくたっていい",
        "heavy-pointer": "ポインターをターゲットから離さないで",
        "x-pattern-hover": "X を点滅させる",
        "hov-o-label": "ホバーできそうな文字があります",
        "h-pattern-unlock": "H の形が作れそう",
        "h-pattern-hover": "H を点滅させる",
        "target-rapid-click": "ターゲットはクッキーかもしれません",
      },
      conditions: {
        observer: "Hov を獲得せずに、Hov ターゲット画面を30秒間見続ける",
        "heavy-pointer": (minutes: number) =>
          `ターゲットを${minutes}分連続でマウスホバーし続ける`,
        "x-pattern-hover": "Hov ターゲットを X の形でホバー状態にする",
        "hov-o-label": (seconds: number) =>
          `HUD の Hov の「o」に${seconds}秒間ホバーする`,
        "h-pattern-unlock": "Hov ターゲットを H の形で解除状態にする",
        "h-pattern-hover": "Hov ターゲットを H の形でホバー状態にする",
        "target-rapid-click": (count: number, windowSeconds: number) =>
          `${windowSeconds}秒以内に${count}回ターゲットをクリックする`,
      },
      effects: {
        observer: "",
        "heavy-pointer": "",
        "x-pattern-hover": (token: number) => `${token} Token`,
        "hov-o-label": (token: number) => `${token} Token`,
        "h-pattern-unlock": (token: number) => `${token} Token`,
        "h-pattern-hover": (token: number) => `${token} Token`,
        "target-rapid-click": (token: number) => `${token} Token`,
      },
    },
  },
  records: {
    tabsAria: "記録カテゴリ",
    tabs: {
      stats: "統計",
      history: "履歴",
      purchases: "購入履歴",
    },
    statsDraftNotice: "統計は準備中です。",
    historyEmpty: "まだ履歴はありません。",
    purchasesEmpty: "まだ購入履歴はありません。",
    productNames: {
      starter_bundle: "スターターバンドル",
      tim_boost_bundle: "Tim ブーストバンドル",
      token_pack_100jpy: "Token 1000",
      token_pack_500jpy: "Token 5500",
      token_pack_2000jpy: "Token 30000",
      token_pack_5000jpy: "Token 65000",
    },
    unknownProduct: (id: string) => id,
    purchaseLine: (date: string, productLabel: string) =>
      `${date}  ${productLabel} を購入`,
    tokenLine: (date: string, delta: string, productLabel: string) =>
      `${date}  Token ${delta} (${productLabel})`,
    tokenDeltaPositive: (amount: string) => `+${amount}`,
    tokenDeltaNegative: (amount: string) => `-${amount}`,
    stats: {
      sectionPlaytime: "プレイ時間",
      onlinePlaytime: "オンラインプレイ時間",
      totalPlaytime: "合計時間",
      sectionHov: "Hov",
      currentHov: "現在の Hov",
      maxHov: "最大到達 Hov",
      sectionTim: "Tim",
      totalTimEarned: "累計獲得 Tim",
      sectionSpecial: "特殊",
      mHovCount: "累計 MHov 回数",
      nHovCount: "累計 NHov 回数",
      oHovPower: "OHov 数",
      oHovFastest: "最速 OHov",
      oHovCurrentTime: "今回の OHov",
      totalHovCoreSpent: "累計 HovCore 消費量",
      sectionAchievements: "実績",
      normalAchievements: "通常実績",
      secretAchievements: "シークレット実績",
      sectionInfo: "情報",
      lastSavedAt: "最終セーブ日時",
      firstPlayedAt: "初回プレイ日時",
      notAvailable: "—",
      formatPlaytime: (totalSeconds: number): string => {
        const s = Math.max(0, Math.floor(totalSeconds));
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (d > 0) return `${d}日 ${h}時間 ${m}分`;
        if (h > 0) return `${h}時間 ${m}分 ${sec}秒`;
        if (m > 0) return `${m}分 ${sec}秒`;
        return `${sec}秒`;
      },
      formatDateTime: (timestamp: number): string => {
        const d = new Date(timestamp);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
      },
    },
  },
  help: {
    tabsAria: "ヘルプ",
    unreadLabel: "新しいヘルプがあります",
    tabs: {
      basics: "遊び方",
      hov: "Hov",
      tim: "Tim",
    },
    basics: {
      pcDescription:
        "Hover Idle は、画面上の対象にマウスポインターを置いて進める放置ゲームです。",
      mobileDescription:
        "Hover Idle は、画面上の対象に仮想ポインターを置いて進める放置ゲームです。",
      items: [
        "Hov ターゲット画面の真ん中のターゲットにホバーして Hov を集めます。",
        "集めた Hov でアップグレードを進めると、Hov の獲得量が増えます。",
        "Hov を集めたり、実績を解除すると何かが起こります。",
        "Hov をいっぱい集めてください！",
      ],
      virtualPointer: {
        heading: "仮想ポインター",
        description:
          "Hover Idle では、タップした場所に仮想ポインターが置かれます。",
        items: [
          "仮想ポインターを置いた場所にホバー効果が発生します。メニューや確認画面では、通常のタップ操作を使います。",
          "仮想ポインターは、最後に置いた場所を覚えています。画面を戻したときも、同じホバー対象に復帰します。",
        ],
      },
      offline: {
        heading: "オフライン報酬",
        description: "オフライン中の経過時間に応じて、Tim を獲得できます。",
        items: [
          "オフライン報酬は、最大24時間分まで獲得できます。",
          "離席中は Hov・リセット・Wall などの通常進行を進めず、報酬対象時間から Tim のみを計算します。",
        ],
      },
      system: {
        heading: "システム画面",
        pcDescription:
          "設定やショップなどのシステム画面はクリックで操作します。",
        mobileDescription:
          "設定やショップなどのシステム画面はタップで操作します。",
      },
    },
    hov: {
      description: "Hov はホバー操作で得る基本リソースです。",
      items: [
        "ターゲットにポインターを置くと Hov を獲得します。",
        "ターゲット左下のアップグレードは Hov の獲得量を伸ばします。",
        "Hov 関連の機能は、進行に応じて段階的に増えます。",
      ],
      mHov: {
        heading: "MHov",
        description:
          "ターゲットを統合（MHov） は、Hov 側の一部進行をリセットして Hov 獲得量を強化する統合操作です。",
        items: [
          "実行すると、ボタン右側の効果量で Hov 獲得量が強化されます。",
          "ボタンの左側は現在倍率です。",
        ],
        resetHeading: "リセットされるもの",
        resetItems: ["所持 Hov", "HG Upgrade", "HnG Upgrade"],
        keepHeading: "リセットされない主なもの",
        keepItems: ["Tim", "Fai", "MHov 効果", "累計統計", "設定"],
        warning:
          "注意: MHov 実行後は Hov 側の短期進行を最初から積み直します。実行前に after 値と reset 対象を確認してください。",
      },
      nHov: {
        heading: "NHov / New Hov",
        description:
          "NHov は Hov 区画の一部をリセットして、Hov 側の強化効果を得るリセットです。",
        items: [
          "Tim 側の進行は保持されます。",
          "初回は HG Upgrade 強化のみを選択します。在庫の壁の初回クリア後は、初回から他の強化先も選べます。",
        ],
        resetHeading: "リセットされる主な要素",
        resetItems: [
          "所持 Hov",
          "Hov Upgrade",
          "HnG Upgrade",
          "MHov 効果",
          "Hov 区画の一時進行",
        ],
        keepHeading: "保持される主な要素",
        keepItems: [
          "Tim",
          "Tim Upgrade",
          "Tim Cell 進行",
          "Tim Skill",
          "NHov 効果",
          "累計統計",
          "設定",
        ],
        warning:
          "注意: NHov 実行後は Hov 側の短期進行を積み直します。実行前に reset 対象を確認してください。",
      },
    },
    tim: {
      description: "Tim は時間経過で得るリソースです。",
      timHovActivation: {
        beforePc:
          "マウスカーソルで Target にホバーすると、Tim が Hov に変換されます。",
        beforeMobile:
          "仮想ポインターで Target にホバーすると、Tim が Hov に変換されます。",
        afterPc:
          "Target にホバーしている間、Tim が Hov に変換されるかどうかを HUD 上の矢印で切り替えられます。",
        afterMobile:
          "仮想ポインターで Target にホバーしている間、Tim が Hov に変換されるかどうかを HUD 上の矢印で切り替えられます。",
      },
      items: [],
    },
  },
  hovSkill: {
    regionAria: "Hov スキル",
    title: "Hov スキル",
    influenceLabel: "影響",
    costLabel: "必要素材",
    externalLabel: "外部補正",
    costResources: {
      hovCore: "HovCore",
    },
    none: "なし",
    stateAcquired: "取得済み",
    stateMissingPrerequisites: "必要条件未達成",
    stateMissingResource: (missing: string, resource: string) =>
      `${resource} 不足 (あと ${missing})`,
    stateHoldToAcquire: (seconds: string) => `${seconds}秒ホバーで取得`,
    holdDurationLabel: "取得時間",
    holdDurationOption: (seconds: string) => `${seconds}秒`,
    names: {
      "amplifier-unlock": "増幅器",
      "hg-exponent-bonus": "Hov 指数強化",
      "hg-hng-simultaneous-purchase-count-base-bonus": "HG・HnG 同時購入",
      "mp-capacity-bonus": "MP 拡張",
      "hov-1-gain-multiplier-bonus": "H1G 倍率強化",
      "amplifier-gain-ohov-multiplier-bonus": "OHov AG 倍率強化",
      "ohov-hov-core-gain-multiplier-bonus": "HovCore 獲得倍率化",
      "mhov-mp-control-unlock": "MHov 制御",
      "nhov-mp-control-unlock": "NHov 制御",
      "variety-pack": "バラエティーパック",
      "amplifier-panel-mp-placement-unlock": "増幅パネル MP 配置",
      "mp-capacity-second-bonus": "MP 再拡張",
      "mp-capacity-core": "MP 容量核",
      "free-mp-core": "余剰 MP 核",
      "wall-cap-relaxation": "壁影響上限緩和",
      "exploration-unlock": "探索",
      "hng-rank-scale-base-bonus": "HnG ランクスケール基礎+",
      "wall-unlock": "壁",
      "ra-plum-amplifier-gain-multiplier-bonus": "RA 梅",
      "ra-bamboo-amplifier-gain-multiplier-bonus": "RA 竹",
      "ra-pine-amplifier-gain-multiplier-bonus": "RA 松",
      "ap-boar-exponent-bonus": "AP 猪",
      "ap-deer-exponent-bonus": "AP 鹿",
      "ap-butterfly-exponent-bonus": "AP 蝶",
    },
    shortLabels: {
      "amplifier-unlock": "Amp",
      "hg-exponent-bonus": "HE+",
      "hg-hng-simultaneous-purchase-count-base-bonus": "HUC+",
      "mp-capacity-bonus": "MP+",
      "hov-1-gain-multiplier-bonus": "H1G×",
      "amplifier-gain-ohov-multiplier-bonus": "AG×",
      "ohov-hov-core-gain-multiplier-bonus": "HC×",
      "mhov-mp-control-unlock": "MHov←",
      "nhov-mp-control-unlock": "NHov←",
      "variety-pack": "VP",
      "amplifier-panel-mp-placement-unlock": "AP-MP",
      "mp-capacity-second-bonus": "MP++",
      "mp-capacity-core": "MP-CC",
      "free-mp-core": "F-MP-C",
      "wall-cap-relaxation": "WCR",
      "exploration-unlock": "Exp",
      "hng-rank-scale-base-bonus": "HnG+",
      "wall-unlock": "Wall",
      "ra-plum-amplifier-gain-multiplier-bonus": "RA-A",
      "ra-bamboo-amplifier-gain-multiplier-bonus": "RA-B",
      "ra-pine-amplifier-gain-multiplier-bonus": "RA-C",
      "ap-boar-exponent-bonus": "AP-α",
      "ap-deer-exponent-bonus": "AP-β",
      "ap-butterfly-exponent-bonus": "AP-γ",
    },
    descriptions: {
      "amplifier-unlock": "増幅器タブを解放します。",
      "hg-exponent-bonus": "HG 指数を強化します。",
      "hg-hng-simultaneous-purchase-count-base-bonus":
        "HG・HnG の同時購入数を強化します。",
      "mp-capacity-bonus": "MP の上限を拡張します。",
      "hov-1-gain-multiplier-bonus": "Hov セル1の獲得量（H1G）を強化します。",
      "amplifier-gain-ohov-multiplier-bonus":
        "OHov に応じて RAG を強化します。",
      "ohov-hov-core-gain-multiplier-bonus": {
        base: "OHov 時の HovCore 獲得量を増やします。",
        scaling: "OHov 時の HovCore 獲得量を OHov 数に応じて増やします。",
      },
      "mhov-mp-control-unlock":
        "MPM で MHov 実行条件用に MP を割り当てられるようになります。",
      "nhov-mp-control-unlock":
        "MPM で NHov 実行条件用に MP を割り当てられるようになります。",
      "variety-pack": "MHov, NHov1, NHov3, NHov4 の成長指数を強化します。",
      "amplifier-panel-mp-placement-unlock":
        "増幅パネルに MP を割り当てられるようになります。",
      "mp-capacity-second-bonus": "MP の上限を拡張します。",
      "mp-capacity-core":
        "MP の上限に応じて OHov 時の HovCore 獲得量を増やします。",
      "free-mp-core":
        "未割当の MP 数に応じて OHov 時の HovCore 獲得量を増やします。",
      "wall-cap-relaxation": "壁6までの報酬影響上限を緩和します。",
      "exploration-unlock": "探索タブを解放します。",
      "hng-rank-scale-base-bonus": "HnG ランクスケールの基礎値を強化します。",
      "wall-unlock": "壁タブを解放します。",
      "ra-plum-amplifier-gain-multiplier-bonus": "RA 獲得量を強化します",
      "ra-bamboo-amplifier-gain-multiplier-bonus":
        "壁突破種類数に応じて RA 獲得量を強化します",
      "ra-pine-amplifier-gain-multiplier-bonus":
        "現在の OHov run 経過時間に応じて RA 獲得量を強化します。",
      "ap-boar-exponent-bonus": "AP 指数を強化します。",
      "ap-deer-exponent-bonus": "増幅器解除数に応じて AP 指数を強化します。",
      "ap-butterfly-exponent-bonus": "AP に応じて AP 指数を強化します。",
    },
  },
  wall: {
    regionAria: "壁",
    title: "壁",
    clearedNotice: "ステップの壁を突破しました",
    rewardLabel: "報酬",
    firstClearRewardLabel: (amount: string) => `初回報酬: +${amount} HovCore`,
    firstClearRewardClaimedLabel: (amount: string) =>
      `初回報酬（獲得済）: +${amount} HovCore`,
    firstClearRewardTextLabel: (reward: string) => `初回報酬: ${reward}`,
    firstClearRewardClaimedTextLabel: (reward: string) =>
      `初回報酬（獲得済）: ${reward}`,
    firstClearRewards: {
      ohovMpControlUnlock: "OHov 実行条件用の MP 割当を解放",
      nhovFirstBranchSelectionUnlock:
        "NHov1 以外を初回 NHov で選択できるようになる",
      amplifierLaDaAvailabilityUnlock: "LA / DA の表示・購入・MP 割当を解放",
    },
    currentMultiplierLabel: "影響",
    currentMultiplierCapReachedLabel: "影響（上限到達）",
    rewardEffectMultiplyFormat: (value: string) => `x${value}`,
    rewardEffectAddFormat: (value: string) => `+${value}`,
    rewardEffectBuiltInOHovAutoTargetFormat: (value: string) => `${value} Hov`,
    rewardEffectRaPropagationInfluenceFormat: (value: string) =>
      `RA 強化伝播 ×${value}`,
    rewardEffectUnacquired: "未獲得",
    bestClearLabel: "最速クリア",
    notClearedYet: "未クリア",
    inProgressLabel: "挑戦中",
    startLabel: "開始",
    startAriaLabel: (wallName: string) => `${wallName}を開始`,
    stopAriaLabel: (wallName: string) => `${wallName}を停止`,
    active: "挑戦中",
    inactive: "停止中",
    startStopChargeDurationLabel: "Wall 開始・停止時間",
    startStopChargeDurationOption: (seconds: string) => `${seconds}秒`,
    walls: {
      "step-wall": {
        name: "ステップの壁",
        constraint: "偶数の Hov セルが解除できなくなる",
        reward: "クリア時間に応じて HG が増加",
        clearedNotice: "ステップの壁を突破しました",
      },
      "price-wall": {
        name: "価格の壁",
        constraint: "NHov2（/n HG コスト）が実行できなくなる",
        reward: "クリア時間に応じて HG・HnG の同時購入数が増加",
        clearedNotice: "価格の壁を突破しました",
      },
      "mp-wall": {
        name: "MP の壁",
        constraint:
          "HovターゲットとHov獲得アップグレードへのMP割当が、専用の1枠だけになる",
        reward: "クリア時間に応じて MP が増加",
        clearedNotice: "MP の壁を突破しました",
      },
      "freshness-wall": {
        name: "鮮度の壁",
        constraint: "MHov, NHov による強化が時間経過で弱化する",
        reward: "クリア時間に応じて MHov 成長指数が増加",
        clearedNotice: "鮮度の壁を突破しました",
      },
      "outer-wall": {
        name: "外側の壁",
        constraint: "壁クリアに必要な Hov が 4.2e720 になる",
        reward: "クリア時間に応じて OHov 自動実行上限が上昇",
        clearedNotice: "外側の壁を突破しました",
      },
      "split-wall": {
        name: "分裂の壁",
        constraint: "ターゲットの分裂は 1 度まで。クリアに 3.9e660 Hov が必要",
        reward: "クリア時間に応じて H1G が増加",
        clearedNotice: "分裂の壁を突破しました",
      },
      "hg-wall": {
        name: "HG の壁",
        constraint: "/1e10 HG, HG^0.5",
        reward: "クリア時間に応じて HG 指数が増加",
        clearedNotice: "HG の壁を突破しました",
      },
      "stock-wall": {
        name: "在庫の壁",
        constraint: "NHov が5回までしか実行できなくなる",
        reward: "クリア時間に応じて在庫の壁の NHov 回数上限が増加",
        clearedNotice: "在庫の壁を突破しました",
      },
      "amplification-wall": {
        name: "増幅の壁",
        constraint: "未解放の増幅器が暴走し、下位の Amplifier Gain を弱化する",
        reward:
          "クリア時間に応じて RA 専用の Amplifier Gain 強化を OA〜DA へ伝播",
        clearedNotice: "増幅の壁を突破しました",
      },
    },
  },
  exploration: {
    regionAria: "探索",
    title: "探索",
    mpUsageAria: "MP 利用状況",
    mpUsage: (used: string, total: string) => `${used} / ${total} MP`,
    areaAria: (biome: string) => `${biome}探索区画`,
    biomes: {
      hov: { name: "Hov バイオーム", compact: "Hb" },
      tim: { name: "Tim バイオーム", compact: "Tb" },
      mat: { name: "Mat バイオーム", compact: "Mb" },
      lev: { name: "Lev バイオーム", compact: "Lb" },
    },
    biomeHeading: (biome: string, level: string) => `${biome} (${level})`,
    effectLabel: (effect: string) => `効果: ${effect}`,
    assignMpSlotAria: (amount: string) => `${amount} MP スロットへ割り当てる`,
    removeMpSlotAria: (amount: string) =>
      `${amount} MP スロットの割当を解除する`,
    timAcceleration: {
      title: (tim: string) => `探索加速 ${tim} Tim`,
      activeEffect: (cost: string, multiplier: string) =>
        `稼働時: -${cost} Tim/秒・探索 ×${multiplier}`,
      toggleAria: (enabled: boolean, insufficientTim: boolean): string =>
        insufficientTim
          ? "探索加速は有効です。Tim が不足しています"
          : enabled
            ? "探索加速を無効にする"
            : "探索加速を有効にする",
    },
    mpSlotPurchase: {
      name: "MP スロット",
      tooltipKind: "探索アップグレード",
      tooltipName: "MP スロット",
      price: (amount: string) => `${amount} HC`,
      completed: "完了",
      description:
        "対象の探索バイオームに MP を追加で割り当てられるようになります。",
      effect: (biome: string, amount: string) =>
        `+ ${biome} ${amount} MP スロット`,
      allUnlockedEffect: "すべての MP スロットを解放済み",
      noCost: "価格未設定",
      noRemainingCost: "—",
      states: { ready: "購入可能" },
    },
    cost: (amount: string) => `コスト ${amount} HovCore`,
    seconds: (seconds: string) => `${seconds}秒`,
    effects: {
      awardOHov: (amount: string) => `+${amount} OHov`,
      addExplorationTime: (duration: string) => `探索時間 +${duration}`,
      multiplyHg: (multiplier: string) => `×${multiplier} HG`,
      raiseBiomeLevel: (amount: string) => `探索バイオームレベル +${amount}`,
    },
    blockedReasons: {
      "exploration-locked": "探索は未解放です",
      "price-unconfigured": "価格未設定",
      "insufficient-hov-core": "HovCore が不足しています",
      "all-mp-slots-unlocked": "すべて解放済み",
      "invalid-mp-slot": "利用できない MP スロットです",
      "mp-slot-locked": "この MP スロットは未解放です",
      "mpm-section-disabled": "探索 MPM セクションは停止中です",
      "already-assigned": "この MP スロットは割当済みです",
      "global-mp-capacity-exceeded": "利用可能 MP が不足しています",
      "not-assigned": "この MP スロットは未割当です",
    },
  },
  timSkill: {
    regionAria: "Tim スキル",
    title: "Tim スキル",
    effectLabel: "効果",
    costLabel: "必要素材",
    none: "なし",
    stateAcquired: "取得済み",
    stateMissingPrerequisites: "必要条件未達成",
    stateMissingResource: (missing: string) => `Tim 不足 (あと ${missing})`,
    stateHoldToAcquire: (seconds: string) => `${seconds}秒ホバーで取得`,
    names: {
      GridIncreaseTim: "GridIncreaseTim",
      GridIncreaseTim2: "GridIncreaseTim2",
    },
    shortLabels: {
      GridIncreaseTim: "GIT",
      GridIncreaseTim2: "GIT^2",
    },
    effects: {
      GridIncreaseTim: "TimTarget が4分割される",
      GridIncreaseTim2: "TimTarget が9分割される",
    },
  },
  resourceHud: {
    ariaLabel: "HoverIdle リソース",
    hovWordH: "H",
    hovWordO: "o",
    hovWordV: "v",
    hovValueSuffix: " ",
    hovWordAriaLabel: "Hov",
    hovOAriaLabel: "シークレット ホバースポット",
    timSeparator: " | ",
    timSuffix: " Tim",
    fai: (value: string) => ` | Fai ${value}`,
    recentRate: (value: string) => `直近レート: ${value} Hov/秒`,
    hovGain: (value: string) => `${value} HG`,
    timGain: (value: string) => `${value} TG`,
    timHovActivationAriaLabel: "TimHov 活性",
    hovCoreHudAriaLabel: "HoverIdle HC と Hov リソース",
    hovCoreAmount: (value: string) => `${value} HC`,
    hovCoreRecentRate: (value: string) => `直近レート: ${value} HC/時`,
    hovAmount: (value: string) => `${value} Hov`,
    oHovPowerAmount: (value: string) => `${value} OHov`,
    hovCoreAmountAriaLabel: "hovCore 数量",
    hovAmountAriaLabel: "Hov 数量",
    oHovPowerAmountAriaLabel: "OHovPower 数量",
  },
  hovView: {
    tabsAria: "Hov バイオームタブ",
    region: "Hov バイオーム",
    mainTab: "ターゲット",
    skillTab: "スキル",
    skillTabLocked: (threshold: string) => `${threshold} < Hov`,
    skillTabUnlockNotice: "Hov スキルタブが解禁されました",
    amplifierTab: "増幅器",
    amplifierTabUnlockNotice: "増幅器タブが解禁されました",
    wallTab: "壁",
    wallTabUnlockNotice: "壁タブが解禁されました",
    explorationTab: "探索",
    amplifierPanelDescription:
      "Hov Skill によって、ここに増幅要素が解放されます。",
    amplifier: {
      hgFormulaSqrt: "HG × √AP",
      hgFormulaExponentBase: "HG × AP",
      upgradeLabel: "アップグレード",
      levelLabel: (level: string) => `Lv. ${level}`,
      outputLevel: (level: string) => `Output Lv. ${level}`,
      amplifyLabel: "増幅",
      lockedLabel: "ロック中",
      corruptedLabel: "暴走中",
      entryTitle: (entryLabel: string) => `${entryLabel} 増幅器`,
      upgradeButtonAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplifier output upgrade`,
      amplifyPanelAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplify panel`,
      corruptedPanelAriaLabel: (entryLabel: string) =>
        `${entryLabel} 増幅器（暴走中・操作不可）`,
      panelMpSlotAriaLabel: (entryLabel: string) =>
        `${entryLabel} 増幅器パネル MP 割当`,
      upgradeMpSlotAriaLabel: (entryLabel: string) =>
        `${entryLabel} 増幅器アップグレード MP 割当`,
      gaugeLabel: "増幅光ゲージ",
      remainingLabel: "Remaining",
      upgradeCostNone: "コスト —",
      upgradeCost: (value: string) => `コスト ${value} HC`,
      tooltipSummary: "増幅光が燃焼し、増幅器ごとの力を生成します。",
      guard: {
        label: "強化ガード",
        description: "HovCore を消費する増幅器アップグレードの誤操作防止時間",
        options: {
          0: "なし",
          1000: "1秒",
          3000: "3秒",
        },
      },
      entries: {
        red: "Red",
        orange: "Orange",
        yellow: "Yellow",
        green: "Green",
        cyan: "Cyan",
        blue: "Blue",
        purple: "Purple",
        light: "Light",
        dark: "Dark",
      },
      gainUpgradeTooltip: {
        effectLabel: "効果",
        effect: (delta: string, shortLabel: string) =>
          `+${delta} ${shortLabel}`,
        afterLabel: (shortLabel: string) => `強化後 ${shortLabel}`,
        after: (current: string, next: string) => `${current} → ${next}`,
        costLabel: "コスト",
        cost: (value: string) => `${value} HC`,
        stateLabel: "状態",
        stateReady: "実行可能",
        stateNeed: (missing: string) => `あと ${missing} HC 必要`,
        byEntry: {
          red: {
            title: "増幅器赤獲得量",
            summary: "増幅器赤から得られる AP が増加します。",
          },
          orange: {
            title: "増幅器橙獲得量",
            summary: "増幅器橙から得られる RAG が増加します。",
          },
          yellow: {
            title: "増幅器黄獲得量",
            summary: "増幅器黄から得られる OAG が増加します。",
          },
          green: {
            title: "増幅器緑獲得量",
            summary: "増幅器緑から得られる YAG が増加します。",
          },
          cyan: {
            title: "増幅器シアン獲得量",
            summary: "増幅器シアンから得られる GAG が増加します。",
          },
          blue: {
            title: "増幅器青獲得量",
            summary: "増幅器青から得られる CAG が増加します。",
          },
          purple: {
            title: "増幅器紫獲得量",
            summary: "増幅器紫から得られる BAG が増加します。",
          },
          light: {
            title: "増幅器光獲得量",
            summary: "増幅器光から得られる AP が増加します。",
          },
          dark: {
            title: "増幅器闇獲得量",
            summary: "増幅器闇から得られる AP が増加します。",
          },
        },
      },
    },
    upgradeCardAria: "Hov 獲得アップグレード詳細",
    hngDrawer: {
      label: "HnG",
      expandAriaLabel: "HnG アップグレードを表示",
      collapseAriaLabel: "HnG アップグレードを隠す",
    },
    upgradeTooltip: {
      effectLabel: "効果",
      costLabel: "コスト",
      stateLabel: "状態",
      effectEnhancementLevels: (count: string, _rawCount: number) =>
        `+強化レベル ${count}`,
      global: {
        title: "Hov 獲得量",
        summary: "得られる Hov が増加します。",
        effectDelta: (delta: string) => `+${delta} HG`,
        effectMultiplier: (multiplier: string) => `${multiplier} HG`,
        afterLabel: "強化後 HG",
      },
      cell: {
        title: (index: number) => `Hov${index} 獲得量`,
        summary: (index: number) =>
          `セル${index}から得られる Hov が増加します。`,
        effectDelta: (index: number, delta: string) => `+${delta} H${index}G`,
        effectMultiplier: (index: number, multiplier: string) =>
          `${multiplier} H${index}G`,
        afterLabel: (index: number) => `強化後 H${index}G`,
      },
      cost: (value: string) => `${value} Hov`,
      batchCost: (unitCost: string, count: string) =>
        `${unitCost} x ${count} Hov`,
      costLabelBatch: (count: string) => `コスト(${count})`,
      wallBlockedCost: "― Hov",
      after: (current: string, next: string) => `${current} → ${next}`,
      stateReady: "実行可能",
      stateNeed: (missing: string) => `あと ${missing} Hov 必要`,
      stateWallBlocked: {
        "step-wall": "ステップの壁により無効",
        "price-wall": "価格の壁により無効",
        "mp-wall": "MP の壁により無効",
        "freshness-wall": "鮮度の壁により無効",
        "outer-wall": "外側の壁により無効",
        "split-wall": "分裂の壁により無効",
        "hg-wall": "HG の壁により無効",
        "stock-wall": "在庫の壁により無効",
        "amplification-wall": "増幅の壁により無効",
      },
    },
    mHov: {
      btnLabel: (before: string, after: string) =>
        `ターゲットを統合: x${before} HG → x${after} HG`,
      btnLabelMobile: (before: string, after: string) =>
        `MHov: x${before} → x${after} HG`,
      btnLabelFreshnessWall: (
        current: string,
        fresh: string,
        effective: string,
      ) => `x${current} HG → x${fresh} HG → x${effective} HG`,
      ariaLabel: "ターゲットを統合",
    },
    nHov: {
      header: "NHov",
      goToNHovPanelAriaLabel: "NHov 面へ移動",
      returnToCellsPanelAriaLabel: "Hov Cells 面へ戻る",
      branchHeading: {
        hgAmplify: "xN HG アップグレード",
        hgDiscount: "/n HG コスト",
        hgNHovEffectsToHng: "HG NHov xN HnG",
        hngScale: "HnG ランクスケール",
      },
    },
  },
  timView: {
    tabsAria: "Tim バイオームタブ",
    region: "Tim バイオーム",
    mainTab: "ターゲット",
    skillTab: "スキル",
    balanceTab: "バランス",
    balanceTabLocked: (threshold: string) => `${threshold} < Tim`,
    balanceTabUnlockNotice: "バランスタブが解禁されました",
    skillTabLocked: (threshold: string) => `${threshold} < Hov`,
    upgradeCardAria: "Tim 獲得アップグレード詳細",
    upgradeTooltip: {
      title: "Tim 獲得量",
      summary: "1秒あたりの Tim 生成量を恒久的に増やします。",
      afterLabel: "強化後 TG",
    },
    upgradeEffect: (value: string) => `+${value} TG`,
    upgradeCost: (value: string) => `コスト: ${value} Tim`,
    upgradeAfter: (current: string, next: string) => `${current} -> ${next}`,
    upgradeStateReady: "実行可能",
    upgradeStateNeed: (missing: string) => `あと ${missing} Tim 必要`,
  },
  upgradeTooltip: {
    kicker: "アップグレード",
    effect: "効果",
    cost: "コスト",
    state: "状態",
  },
  cellUpgrade: {
    upgradeEffect: (gain: string) => `+${gain}`,
    upgradeAfter: (current: string, next: string) => `${current} → ${next}`,
    actionPrefix: "コスト: ",
    upgradeCost: (value: string, unit: string) => `${value} ${unit}`,
    upgradeStateReady: "実行可能",
    upgradeStateNeed: (missing: string, unit: string) =>
      `あと ${missing} ${unit} 必要`,
  },
  hoverTarget: {
    hovCellAria: (index: number) => `Hov target ${index}`,
    timCellAria: (index: number) => `Tim target ${index}`,
    mpSlotAria: (index: number) => `Mouse pointer slot ${index}`,
    wallMpSlotAria: (target: string) => `MP の壁専用スロット: ${target}`,
    lockBadge: "🔒",
  },
  balance: {
    regionAria: "HovTim バランス",
    title: "HovTim バランス",
    total: (value: string) => `HovTim ${value}`,
    gain: (value: string) => `HovTimG ${value}`,
    unassigned: (value: string) => `未割当 ${value}`,
    resourceSliderAria: "Hov Tim allocation ratio",
    gainSliderAria: "HG TG allocation ratio",
    assignAria: "ホバーで割当",
    unassignAria: "ホバーで割当解除",
    assign: "割当",
    unassign: "割当解除",
    assignHint: "ホバーで追加",
    unassignHint: "ホバーで解除",
    gainUnlockNotice: "HG/TG 配分が解禁されました",
  },
  payment: {
    title: "ショップ",
    categoriesAria: "ショップカテゴリ",
    tokenSummary: (token: string) => `所持 Token: ${token}`,
    tabs: {
      effects: "効果",
      tokens: "トークン",
      bundles: "バンドル",
      credits: "Credits",
    },
    effectsDescription:
      "Token を使って永続効果を購入できます。完全初期化を行っても、この画面の購入効果は保持されます。",
    acquiredHeading: "獲得済み効果",
    acquiredEmpty: "まだ獲得済みの効果はありません。",
    acquiredEffectEntry: (name: string, effect: string) =>
      `${name} — ${effect}`,
    tokensDescription:
      "Token を購入できます。いつでも何度でも購入可能で、獲得した Token はショップ内の永続効果の購入などに使えます。",
    bundlesDescription:
      "Bundles は entitlement として一度だけ購入され、ローカル進行への報酬適用も一回だけ行われます。",
    tokensPlaceholder: "トークン (準備中)",
    headers: {
      effectCost: "消費",
      effectName: "名前",
      effectValue: "永続効果",
      bundleName: "name(sku)",
      bundleContents: "contents",
      bundlePrice: "price",
      tokenAmount: "Token",
      tokenPrice: "price",
    },
    effectCostValue: (cost: string) => `${cost} Token`,
    effectMaxedCost: "—",
    effectOwnedCount: (count: string, max: string) => `${count}/${max}`,
    effectTotalMpCapacity: (amount: string) => `+${amount} MP`,
    effectTotalHovGainPercent: (percent: string) => `+${percent}% HG`,
    effectTotalTimGainPercent: (percent: string) => `+${percent}% TG`,
    tokenPackAmount: (token: string) => `${token} Token`,
    statusPurchasedEffect: "永続効果を購入しました。",
    statusPurchasedBundle: "Bundle を購入し、報酬を適用しました。",
    statusPurchasedTokenPack: "Token を購入しました。",
    statusAlreadyPurchasedEffect: "この永続効果は購入済みです。",
    statusAlreadyPurchasedBundle: "この Bundle は購入済みです。",
    statusInsufficientToken: "Token が不足しています。",
    statusBundleCheckoutFailed: "Bundle の購入に失敗しました。",
    statusTokenPackCheckoutFailed: "Token の購入に失敗しました。",
    statusCheckoutUnavailable:
      "この配信では購入手続きが利用できません。Token と Bundle の購入はできません。",
    permanentNames: {
      addMousePointer: "Add Mouse Pointer",
      hovGainPercent: "HG 強化",
      timGainPercent: "TG 強化",
    },
    permanentEffects: {
      addMousePointer: "+1 MP",
      hovGainPercent: "+10% HG",
      timGainPercent: "+5% TG",
    },
    bundleNames: {
      starter_bundle: "スターターバンドル",
      tim_boost_bundle: "Tim ブーストバンドル",
    },
    bundlePartSeparator: " / ",
    bundlePartToken: (token: string) => `${token} Token`,
    bundlePartHov: (hov: string) => `+${hov} Hov`,
    bundlePartTim: (tim: string) => `+${tim} Tim`,
    signInRecommendation: {
      title: "サインインのおすすめ",
      body: [
        "購入状態を安全に復元できるよう、サインインをおすすめします。",
        "ゲストのまま購入することもできますが、ブラウザデータ削除や別端末利用時に復旧が難しくなる場合があります。",
      ],
      actions: {
        signIn: "サインインする",
        continueAsGuest: "ゲストのまま購入する",
        cancel: "キャンセル",
      },
    },
    purchaseLegalNotice: {
      title: "購入前の確認",
      agreement:
        "購入を続行することで、利用規約・プライバシーポリシーに同意したものとみなされます。",
      details: "返金条件と事業者情報は、以下のページから確認できます。",
      stripeFootnote:
        "Stripe Checkout 導入時は、Checkout 側にも同じポリシーとサポート連絡先を表示する想定です。",
      links: {
        terms: "利用規約",
        privacy: "プライバシーポリシー",
        commerce: "特定商取引法に基づく表記",
      },
    },
    credits: {
      title: "Credits",
      badge: "Draft",
      description:
        "HoverIdle 向けの法務・運営ドラフトです。Shop の購入導線から参照できる前提で、ゲーム固有ルール、Stripe 決済、保存仕様を整理しています。",
      sectionsAria: "Credits カテゴリ",
      sections: {
        operations: {
          label: "運営情報",
          title: "運営情報",
          lead: "HoverIdle の運営主体、問い合わせ先、購入サポートの基本情報です。法的な正式表記は特定商取引法に基づく表記を優先します。",
          items: [
            "運営主体: AkiWeb Hakoniwa",
            "サービス名: HoverIdle",
            "サイト URL: https://akiweb8528.net/",
            "サポート窓口: akiweb8528@gmail.com",
            "対応言語: 日本語 / English",
            "課金導線: 有料商品の購入時は Stripe Checkout などの外部決済画面を利用する想定です。",
          ],
          note: "注意: 代表者名、電話番号、所在地などの正式な事業者表記は特定商取引法に基づく表記で案内します。",
        },
        terms: {
          label: "利用規約",
          title: "利用規約",
          lead: "HoverIdle 専用の利用規約ドラフトです。放置ゲーム特有のセーブ仕様、バランス調整、課金、禁止行為を前提に整理しています。",
          items: [
            "本サービスはブラウザで提供される放置系ゲームであり、ゲーム内容、提供範囲、公開範囲は運営判断で変更される場合があります。",
            "セーブデータは原則として利用端末・ブラウザの localStorage 等に保存されます。現時点でクラウド保存は保証しません。",
            "ブラウザ設定の変更、端末故障、キャッシュ削除、ストレージ消去等によるデータ消失について、運営は復旧を保証しません。",
            "オフライン報酬、進行速度、報酬倍率、価格、必要素材、アンロック条件その他バランス値は、購入前後を問わず調整される場合があります。",
            "有料アイテムは購入時に表示された内容に従って付与されます。サブスク型商品を導入する場合は、更新周期、料金、解約方法を別途表示します。",
            "デジタル商品の性質上、法令上必要な場合または購入処理の不具合等を除き、返金・取消しには応じません。サブスクを導入した場合の中途解約は次回更新停止を基本とします。",
            "bot、macro、自動化ツール、スクリプト、改ざん、チート、脆弱性悪用、リバースエンジニアリング、決済不正、迷惑行為を禁止します。",
            "違反または不正の疑いがある場合、進行状況、課金状態、利用資格の制限または停止を行うことがあります。",
            "保守、障害対応、法令対応、運営上の判断により、事前通知の有無を問わずサービスの全部または一部を停止・変更できます。",
            "運営は、故意または重過失を除き、本サービスの利用または利用不能により生じた損害について責任を負いません。",
            "本規約は日本法に準拠し、本サービスに関する紛争は神戸地方裁判所を第一審の専属的合意管轄裁判所とします。",
          ],
          note: "注意: 正式公開時には条文形式に整え、定義、通知方法、規約変更手続を含めて確定します。",
        },
        privacy: {
          label: "プライバシーポリシー",
          title: "プライバシーポリシー",
          lead: "HoverIdle 専用のプライバシーポリシードラフトです。Stripe 決済、ゲーム進行データ、ブラウザ保存を前提に記載しています。",
          items: [
            "取得する情報: メールアドレス、決済に関する識別情報、問い合わせ内容、Cookie、localStorage、アクセス解析情報、ゲーム進行データ、端末・ブラウザ情報、IP アドレス。",
            "利用目的: サービス提供、課金状態の確認、アイテム付与、本人確認、不正防止、問い合わせ対応、品質改善、障害調査。",
            "ゲーム進行データと設定情報は、原則として利用端末のブラウザストレージに保存されます。",
            "クラウド保存を導入する場合は、保存先、保持期間、復旧方針を本ポリシーまたは別途画面で案内します。",
            "決済には Stripe を利用します。決済情報の処理は Stripe のシステムおよび同社ポリシーにも従います。",
            "ホスティング先、ログ基盤、問い合わせ基盤などの委託先を利用する場合、必要範囲で情報を共有することがあります。",
            "アクセス解析ツールを導入する場合は、導入先の名称、取得情報、停止方法を本ポリシーに追記します。",
            "Stripe その他の外部事業者を利用する場合、必要に応じて日本国外で情報が処理されることがあります。",
            "法令に基づく場合、不正対策に必要な場合、または権利保護に必要な場合を除き、本人同意なく第三者提供しません。",
            "開示、訂正、削除、利用停止等の請求は、本人確認の上で対応します。",
          ],
          note: "注意: 正式公開前に、実際のホスティング先、解析ツール名、保存期間、問い合わせ導線の確定値へ差し替えます。",
        },
        commerce: {
          label: "特定商取引法に基づく表記",
          title: "特定商取引法に基づく表記",
          lead: "AkiWeb Hakoniwa の法的表記をベースに、HoverIdle のデジタル商品販売条件をカバーするドラフトです。",
          items: [
            "事業者名称: AkiWeb Hakoniwa",
            "サービス名: HoverIdle",
            "代表者: お問い合わせいただければ、遅滞なく開示いたします。",
            "所在地: 〒662-0917 兵庫県西宮市与古道町6-6",
            "電話番号: お問い合わせいただければ、遅滞なく開示いたします。",
            "メールアドレス: akiweb8528@gmail.com",
            "販売価格: 各商品ページまたは Shop 画面に税込価格で表示します。",
            "販売価格以外の必要料金: インターネット接続料金、通信料金等はお客様負担です。",
            "支払方法: Stripe を通じたクレジットカードその他、購入画面に表示する方法。",
            "支払時期: 購入手続完了時。サブスク型商品を導入した場合は各更新時。",
            "提供時期: 決済完了後、通常は直ちにゲーム内へ反映します。通信障害等で遅延する場合があります。",
            "返品・返金: デジタル商品の性質上、法令上必要な場合または未提供・重複課金等を除き、原則として返品・返金は行いません。",
            "中途解約: サブスク型商品を導入した場合は、別途表示する方法で次回更新前までに解約できます。",
            "動作環境: 主要ブラウザ（Chrome、Firefox、Safari）の最新版を推奨します。",
          ],
          note: "注意: Stripe Checkout には、返金ポリシー、サポート連絡先、利用規約、プライバシーポリシーのリンクも掲出する想定です。",
        },
      },
    },
  },
  system: {
    loadFailureMessage:
      "セーブデータを読み込めなかったため、新規開始しました。",
  },
};

export type AppMessages = typeof jaMessages;
